import { all, call, put, takeLatest } from "redux-saga/effects";
import request from "@util/net/request";
import axios, { AxiosResponse } from "axios";
import { loadStyleHintAllStylingSaga } from "@redux/saga/allStyling/index";
import {
  GET_STYLING_STYLEHINT_LARGE_IMAGE_LIST,
  GET_STYLING_STYLEHINT_LIST,
  StylehintListActions
} from "@redux/reducer";
import {
  StyleHintAllStylingInfo,
  LargeStylehintStylingPayload,
  LargeStylehintStylingResponse,
  SHlargeImages,
  SHNormalImages,
  STYLING_SORT_BY,
  StylehintListPayload,
  StylehintListResponse
} from "@type/index";

export function* loadStylehintListSaga(action: {
  payload: StylehintListPayload;
}) {
  try {
    const payload: StylehintListPayload = action.payload;
    if (payload.order === STYLING_SORT_BY.PUBLISHED_AT_DESC) {
      payload.sortByPriority = true;
    }
    yield put(StylehintListActions.setStylehintListErrorCode(""));
    yield put(StylehintListActions.getStylingStylehintListLoading(true));
    const res: AxiosResponse<StylehintListResponse> = yield call(
      request.get,
      "/styles/images/sh/normal",
      {
        params: { ...payload }
      }
    );
    if (res.data.status === "nok") {
      yield put(StylehintListActions.setStylehintListErrorCode("503"));
    } else {
      yield put(
        StylehintListActions.getStylingStylehintListSuccess(res.data.result)
      );
    }
  } catch (error) {
    if (!axios.isCancel(error)) {
      yield put(StylehintListActions.setStylehintListErrorCode("503"));
    }
  }
}

export function* loadStylehintLargeListSaga(action: {
  payload: LargeStylehintStylingPayload;
}) {
  try {
    const payload: LargeStylehintStylingPayload = action.payload;
    yield put(StylehintListActions.getStylehintLargeImageListLoading(true));
    const [res, allStylingRes]: [
      AxiosResponse<LargeStylehintStylingResponse>,
      StyleHintAllStylingInfo | undefined
    ] = yield all([
      call(request.get, "/styles/images/sh/large", {
        params: { genderCode: payload.genderCode }
      }),
      call(loadStyleHintAllStylingSaga, {
        style_gender: payload.style_gender
      })
    ]);
    const stylehintLargeList = res.data.result;

    let resList: AxiosResponse<StylehintListResponse> | undefined;
    let isSearchHashtagMissing = false;
    if (payload.searchHashtag) {
      const hashtagFromQuery = payload.searchHashtag.toLowerCase();
      const target = stylehintLargeList.some(
        (item) => (item.hashtag || "").toLowerCase() === hashtagFromQuery
      );
      if (!target) {
        isSearchHashtagMissing = true;
        const { genderCode, searchHashtag, sortByPriority, ...restPayload } =
          payload;
        resList = yield call(request.get, "/styles/images/sh/normal", {
          params: {
            ...restPayload,
            limit: 1,
            order: STYLING_SORT_BY.EC_LIKES_31DAYS_DESC
          }
        });
      }
    }

    const showAllStyling =
      stylehintLargeList.length > 0 || isSearchHashtagMissing;

    // Only fetch the unfiltered (no filter, default sort) style list when the
    // UGC Admin image is not set and the "All Styling" tile will be rendered,
    // so the fallback image never changes with the current filter or sort.
    let allStylingImageUrl = allStylingRes?.image_url || "";
    if (showAllStyling && !allStylingRes?.image_url) {
      try {
        const normalListRes: AxiosResponse<StylehintListResponse> = yield call(
          request.get,
          "/styles/images/sh/normal",
          {
            params: {
              departmentIds: payload.departmentIds,
              order: STYLING_SORT_BY.PUBLISHED_AT_DESC,
              sortByPriority: true,
              limit: 10,
              offset: 0
            }
          }
        );
        const normalImages: Array<SHNormalImages> =
          normalListRes?.data?.result?.images || [];
        // >= 10 items -> the 10th one; otherwise -> the last one
        const index = Math.min(9, normalImages.length - 1);
        allStylingImageUrl = normalImages[index]?.styleImageUrl || "";
      } catch {
        allStylingImageUrl = "";
      }
    }

    const allStyling: SHlargeImages = {
      hashtag: "",
      imageUrl: allStylingImageUrl
    };

    const largeList = showAllStyling ? [allStyling, ...stylehintLargeList] : [];
    yield put(
      StylehintListActions.getStylehintLargeImageListSuccess({
        largeList,
        normalList: resList?.data?.result || undefined,
        searchHashtag: payload.searchHashtag
      })
    );
  } catch (error) {
    yield put(StylehintListActions.getStylehintLargeImageListLoading(false));
  }
}

export function* watchLoadStylingListSaga() {
  yield takeLatest<any>(GET_STYLING_STYLEHINT_LIST, loadStylehintListSaga);
  yield takeLatest<any>(
    GET_STYLING_STYLEHINT_LARGE_IMAGE_LIST,
    loadStylehintLargeListSaga
  );
}
